"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="h-12 w-3/4 bg-muted/30 rounded-lg animate-pulse mb-6" />
        <div className="h-96 bg-muted/30 rounded-xl animate-pulse mb-8" />
        <div className="space-y-4">
          <div className="h-4 bg-muted/30 rounded animate-pulse" />
          <div className="h-4 bg-muted/30 rounded animate-pulse w-5/6" />
          <div className="h-4 bg-muted/30 rounded animate-pulse w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Failed to load post content.</p>
          <Link to="/HomePage">
            <Button variant="outline">
              <ArrowLeft />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Post not found.</p>
          <Link to="/HomePage">
            <Button variant="outline">
              <ArrowLeft />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-8">
        <Link to="/HomePage">
          <Button variant="ghost" className="mb-6 -ml-3">
            <ArrowLeft />
            Back to Blog
          </Button>
        </Link>

        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-6">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}
              </time>
            </div>
          )}

          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}
        </div>

        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <Tag className="size-4 text-muted-foreground" />
            {post.categories.map((category, i) => (
              <span
                key={category.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-light text-primary"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {post.featuredImage && (
        <div className="mb-10 rounded-xl overflow-hidden shadow-medium">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="my-10" />

      {post.author && (
        <div className="flex items-start gap-4 p-6 rounded-xl bg-muted-light-background">
          <Avatar className="size-16">
            {post.author.avatar && post.author.avatar["96"] ? (
              <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
            ) : (
              <AvatarFallback className="bg-primary text-foreground-primary text-lg">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>

          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">About {post.author.name}</h3>
            <p className="text-sm text-muted-foreground">
              Author at ABA With Passion Inc
            </p>
          </div>
        </div>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-10">
          <h4 className="font-semibold text-sm text-muted-foreground mb-3">Tags</h4>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 rounded-md text-xs bg-secondary-light text-secondary hover:bg-secondary/10 transition-colors"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = (window as any).wvcClient?.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}